import { getAccessToken } from '../authentication/supabaseService';
import { TranslatedText } from '../recipes/recipesService';

export interface ChecklistTask {
  taskId: string;
  title: string;
  titleTranslations?: TranslatedText;
  pos: number;
  isActive: boolean;
  checked: boolean;
  checkedBy?: string;
  checkedAt?: string;
}

export interface Checklist {
  id: string;
  restaurantId: string;
  name: string;
  nameTranslations: TranslatedText;
  tasks: ChecklistTask[];
  assignedTo: string[];
  clearsDaily: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface ChecklistCreateRequest {
  restaurantId: string;
  name: string;
  nameTranslations?: TranslatedText;
  tasks: ChecklistTask[];
  assignedTo?: string[];
  clearsDaily?: boolean;
}

export interface ChecklistUpdateRequest {
  id: string;
  name?: string;
  nameTranslations?: TranslatedText;
  tasks?: ChecklistTask[];
  assignedTo?: string[];
  clearsDaily?: boolean;
}

export async function fetchChecklistsByRestaurant(
  restaurantId: string
): Promise<Checklist[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/checklists/restaurant/${restaurantId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch checklists: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return data.map((item: any) => ({
    ...item,
    createdAt: new Date(item.createdAt),
    updatedAt: new Date(item.updatedAt),
  }));
}

export async function fetchChecklistById(id: string): Promise<Checklist> {
  const token = await getAccessToken();
  const res = await fetch(`/api/checklists/${id}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch checklist: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return {
    ...data,
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt),
  };
}

export async function createChecklist(
  request: ChecklistCreateRequest
): Promise<Checklist> {
  const token = await getAccessToken();
  const res = await fetch('/api/checklists/create', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request),
  });
  if (!res.ok) {
    throw new Error(`Failed to create checklist: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return {
    ...data,
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt),
  };
}

export async function updateChecklist(
  request: ChecklistUpdateRequest
): Promise<Checklist> {
  const token = await getAccessToken();
  const res = await fetch('/api/checklists/update', {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(request),
  });
  if (!res.ok) {
    throw new Error(`Failed to update checklist: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return {
    ...data,
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt),
  };
}

export async function updateChecklistTasks(
  checklistId: string,
  tasks: ChecklistTask[]
): Promise<Checklist> {
  const token = await getAccessToken();
  const res = await fetch(`/api/checklists/${checklistId}/tasks`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ tasks }),
  });
  if (!res.ok) {
    throw new Error(`Failed to update checklist tasks: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return {
    ...data,
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt),
  };
}

export async function completeChecklist(checklistId: string): Promise<Checklist> {
  const token = await getAccessToken();
  const res = await fetch(`/api/checklists/${checklistId}/complete`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to complete checklist: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return {
    ...data,
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt),
  };
}

export async function deleteChecklist(id: string): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/checklists/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to delete checklist: ${res.status} ${res.statusText}`);
  }
}

